import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// One-time claim page. The paying user arrives here from their Stripe/PayPal
// welcome email. If they're not logged in we bounce them through /login with
// a redirect back; if they land here already signed in we hit the claim
// endpoint immediately and show a success card. Second visits are idempotent
// and show "already claimed" copy.
//
// URL shape: /claim/:slug/:cycle e.g. /claim/advisor_toolkit/annual
export default function ClaimPass() {
    const { slug, cycle } = useParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [state, setState] = useState({ loading: false, result: null, err: "" });
    // React.StrictMode double-invokes effects in dev, which would fire two
    // sequential POST /claim calls and make the SECOND (idempotent) response
    // win — so the first-time visitor sees "already claimed" instead of the
    // success card. Guard with a mount-scoped ref so we only fire once.
    const claimedRef = useRef(false);

    const claimUrl = `/claim/${encodeURIComponent(slug)}/${encodeURIComponent(cycle)}`;

    useEffect(() => {
        if (!user) return; // guest — the auth wall below handles it
        if (claimedRef.current) return;
        claimedRef.current = true;
        (async () => {
            setState({ loading: true, result: null, err: "" });
            try {
                const { data } = await api.post(`/claim/${slug}/${cycle}`);
                setState({ loading: false, result: data, err: "" });
            } catch (e) {
                setState({ loading: false, result: null, err: formatApiError(e) });
            }
        })();
    }, [user, slug, cycle]);

    const isAnnual = cycle === "annual";
    const passLabel = isAnnual ? "1-Year pass" : "1-Month pass";

    // Guest wall — friendly card, both login and register offered, both
    // preserve the redirect so post-auth we come right back and auto-claim.
    if (!user) {
        return (
            <div className="max-w-2xl mx-auto px-6 md:px-10 py-16" data-testid="claim-page-guest">
                <div className="overline mb-4">Claim your pass</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight">
                    You&apos;re one step from your {passLabel}.
                </h1>
                <p className="mt-6 text-[#c2c2c2] leading-relaxed">
                    Sign in (or create a free account) and we&apos;ll credit your client
                    scans to your account immediately. It&apos;s a one-time claim tied to
                    the account you sign in with, so use the email you plan to keep.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <Link
                        to={`/login?redirect=${encodeURIComponent(claimUrl)}`}
                        className="btn-primary"
                        data-testid="claim-guest-login-btn"
                    >
                        Sign in to claim
                    </Link>
                    <Link
                        to={`/register?redirect=${encodeURIComponent(claimUrl)}`}
                        className="btn-secondary"
                        data-testid="claim-guest-register-btn"
                    >
                        Create an account
                    </Link>
                </div>
                <p className="mt-10 text-xs text-[#7a7a7a]">
                    This link is safe to bookmark — if anything goes wrong, contact your advisor
                    or SmartNote2Self support and we&apos;ll help.
                </p>
            </div>
        );
    }

    // Authed — show the outcome.
    return (
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-16" data-testid="claim-page-authed">
            {state.loading && (
                <div className="text-[#c2c2c2] uppercase tracking-widest text-sm" data-testid="claim-loading">
                    Claiming your pass…
                </div>
            )}
            {state.err && (
                <div
                    className="border border-[#c95151] bg-[#1a0e0e] p-6 md:p-8"
                    data-testid="claim-error"
                >
                    <div className="overline text-[#c95151]">Couldn&apos;t claim this pass</div>
                    <p className="mt-3 text-sm text-[#e2e2e2]">{state.err}</p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            className="btn-secondary"
                            onClick={() => nav("/dashboard")}
                            data-testid="claim-error-dashboard-btn"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                </div>
            )}
            {state.result && !state.result.already_claimed && (
                <div className="border border-[#2ec5b6] bg-gradient-to-b from-[#0f1f1c] to-[#0a0a0a] p-8 md:p-10" data-testid="claim-success">
                    <div className="overline text-[#2ec5b6]">Confirmed</div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-4">
                        {state.result.scans_credited} client scans added to your account.
                    </h1>
                    <p className="mt-6 text-[#c2c2c2] leading-relaxed">
                        Your <strong>{state.result.plan_display_name}</strong> {passLabel} is
                        active. Start a new assessment whenever you&apos;re ready — the credit
                        stays on your account until you use it.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <Link to="/assessment" className="btn-primary" data-testid="claim-start-assessment-btn">
                            Begin a client scan
                        </Link>
                        <Link to="/dashboard" className="btn-secondary" data-testid="claim-dashboard-btn">
                            Go to Dashboard
                        </Link>
                    </div>
                </div>
            )}
            {state.result && state.result.already_claimed && (
                <div className="border border-[#2a2a2a] bg-[#141414] p-8 md:p-10" data-testid="claim-already">
                    <div className="overline">Already claimed</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-4">
                        You&apos;ve already redeemed this pass.
                    </h1>
                    <p className="mt-4 text-[#c2c2c2] leading-relaxed">
                        {state.result.message ||
                            "Your credit is already on your account — head to Dashboard to run your next scan."}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <Link to="/dashboard" className="btn-primary" data-testid="claim-already-dashboard-btn">
                            Go to Dashboard
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
